import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import useEmblaCarousel from 'embla-carousel-react';
import React, { useCallback, useEffect, useState } from 'react';
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button';
import { Link } from 'react-router-dom';
import { useTranslation } from '../../hook/Translation';

// Define the expected props structure for clarity
const defaultOptions = { loop: false, align: 'start' };

export default function ProgressCarousel({ slides, options = defaultOptions, buttonSize, data, noUnderLine, textSize, maxWidth, showLine, spacing }) {
    const { t } = useTranslation();
    // 1. Embla Carousel Setup
    const [emblaRef, emblaApi] = useEmblaCarousel(options);
    const [scrollProgress, setScrollProgress] = useState(0);

    // 2. Scroll Progress Logic
    const onScroll = useCallback(() => {
        if (!emblaApi) return;
        // emblaApi.scrollProgress() returns 0 (start) to 1 (end). This is correct for low to high progress.
        const progress = Math.max(0, Math.min(1, emblaApi.scrollProgress()));
        setScrollProgress(progress);
    }, [emblaApi]);

    // 3. Attach/Detach Scroll Listener
    useEffect(() => {
        if (!emblaApi) return;
        onScroll(); // Set initial progress
        emblaApi.on('scroll', onScroll);
        emblaApi.on('reInit', onScroll);
        return () => {
            emblaApi.off('scroll', onScroll);
            emblaApi.off('reInit', onScroll);
        };
    }, [emblaApi, onScroll]);

    // --- Styling for multiple items and full-width structure ---

    const emblaContainerStyle = {
        display: 'flex',
        touchAction: 'pan-y',
        // This negative margin counter-acts the `paddingLeft` on the slides for alignment
        marginLeft: '-10px',
    };

    // REQUIRED: Embla slide must use flex properties to define its width
    const slideStyle = {
        paddingBottom: { md: 'unset', xs: '10px' },
        // Show 3 slides (33.33%). Adjust this value to control how many slides are visible.
        flex: { md: '0 0 25%', xs: '0 0 90%' },
        minWidth: 0,
        paddingLeft: '20px', // Adds spacing between slides
    };
    const slideFirst = {
        paddingBottom: { md: 'unset', xs: '10px' },
        // Show 3 slides (33.33%). Adjust this value to control how many slides are visible.
        flex: { md: '0 0 25%', xs: '0 0 90%' },
        minWidth: 0,
        paddingLeft: '10px',
    };

    // --- Render Structure ---
    return (
        // Main container must be relative for the absolute overlay positions
        <Box sx={{ position: 'relative', maxWidth: maxWidth ? maxWidth :'90%', mx: 'auto' }}>

            {/* Carousel Container */}
            <Box className="embla" sx={{ overflow: 'hidden', position: 'relative' }}>
                <Box className="embla__viewport" ref={emblaRef} sx={{ overflow: 'hidden' }}>
                    {/* FIX: Replaced Grid container with Box and applied flex styles */}
                    <Box className="embla__container" sx={emblaContainerStyle}>
                        {slides.map((slide, index) => (
                            <Box className="embla__slide" key={index} sx={index === 0 ? slideFirst : slideStyle}>
                                <Grid container spacing={spacing ? spacing : 2}>
                                    <Grid size={12} height={'300px'} bgcolor={'#DBDAD9'}>
                                    {
                                        slide.imageUrl &&
                                        <Box component={'img'} src={slide.imageUrl} sx={{ width: '100%', objectFit: 'cover', height: '100%' }} />
                                    }
                                    </Grid>
                                    {
                                        slide.text &&
                                        <Grid size={textSize ?? { md: 12, xs: 12 }}>
                                            <Typography variant="h4" fontWeight={'bold'} fontSize={{ md: '24px', xs: '1rem' }} color='text.secondary'>{slide.text}</Typography>
                                        </Grid>
                                    }
                                    {
                                        slide.desc &&
                                        <Grid size={textSize ?? { md: 12, xs: 12 }}>
                                            <Typography variant="body1" fontSize={{ md: '1rem', xs: '1rem' }} color='text.secondary'>{slide.desc}</Typography>
                                        </Grid>
                                    }
                                    {
                                        slide.price &&
                                        <Grid size={textSize ?? { md: 12, xs: 12 }}>
                                            <Typography variant="body1" fontSize={{ md: '1rem', xs: '1rem' }} fontWeight={'bold'} color='text.secondary'>{slide.price}</Typography>
                                        </Grid>
                                    }
                                    {
                                        slide.buttonUrl &&
                                        <Grid size={buttonSize ?? 12}>
                                            <Button LinkComponent={Link} to={slide.buttonUrl} fullWidth variant='outlined' className='borderRadiusFlat borderWidth2 buttonPaddingY10' color='text.secondary' sx={{ fontSize: '17px', maxHeight: '50px', width: { md: '246px', xs: '100%' } }}>{t('learn_more')}</Button>
                                        </Grid>
                                    }
                                </Grid>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* Left Fading Overlay */}
                {/* <Box sx={{ position: 'absolute', top: 0, left: 0, height: '100%', width: '50px', pointerEvents: 'none', backgroundImage: `linear-gradient(to right, white, transparent)`, }}/> */}

                {/* Right Fading Overlay */}
                {/* <Box sx={{ position: 'absolute', top: 0, right: 0, height: '100%', width: '50px', pointerEvents: 'none', backgroundImage: `linear-gradient(to left, white, transparent)` }} /> */}
            </Box>
            {
                noUnderLine ??
                <Box sx={{ height: '5px', borderRadius: '20px', width: { md: '60%', xs: '90%' }, marginRight: 'auto', backgroundColor: 'grey.300', mt: { md: '80px', xs: '50px' } }} >
                    <Box sx={{ height: '100%', width: `${scrollProgress * 100}%`, backgroundColor: 'text.secondary', borderRadius: '2px', transition: 'width 0.2s ease-out' }} />
                </Box>
            }

        </Box>
    );
}